import * as React from 'react';
import {FormEvent} from 'react';
import Button from "@components/common/button/Button";
import Typography from '@material-ui/core/Typography';
import {IFamilyMember} from '@app-types/websocket/family.types';
import TranslateNew from '@translate/TranslateNew';

import {WithStyles, withStyles} from '@material-ui/core/styles';
import {GuideStyles} from '../guide.styles';
import ProgressBar from "@components/common/progressBar/progressBar";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import clsx from "clsx";
import Select from "@components/common/select/Select";
import cordovaContext from "../../../cordovaContext";
import {MenuItem} from "@material-ui/core";
import TemplateCalendar from "@components/guide/components/templateCalendar.component";
import { primaryColor, secondaryColor } from 'src/main.style';

const tn: TranslateNew = TranslateNew.getInstance();

interface IStepTemplateCustomProps extends WithStyles<typeof GuideStyles> {
    setTemplate: (template: boolean[]) => void;
    setStep: (step: number) => void;
    step: number;
    progress?: number | undefined;
    toggleSnackBar: (state: boolean) => void;
    setStepErrorType: (type: string) => void;
    firstParent: IFamilyMember;
    secondParent: IFamilyMember;
    goBack: () => void;
    style?:React.CSSProperties;
    localization: 'en'|'se';
}

interface IStepTemplateCustomState {
    formValues: {
        template: boolean[];
        weeks: number;
    };
    formErrors: {
        template?: string | undefined,
        weeks?: string | undefined,
    };
    oneChange:boolean;
}

class StepTemplate extends React.Component<IStepTemplateCustomProps, IStepTemplateCustomState> {
    constructor(props: any) {
        super(props);

        this.state = {
            formValues: {
                template: new Array(14).fill(true),
                weeks: 2,
            },
            formErrors: {
                template: undefined,
                weeks: undefined,
            },
            oneChange:false,
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.validateTemplate = this.validateTemplate.bind(this);
        this.resetToDefault = this.resetToDefault.bind(this);
        this.changeWeeks = this.changeWeeks.bind(this);
    }

    public render() {
        const {formValues, formErrors} = this.state;
        const {classes: style, step, firstParent, secondParent} = this.props;
        const isCordovaApp = this.context;
        const weeksOptions = Array.from({length: 14}, (_, index) => index + 1);

        let allDaysTheSame = true;

        if (formValues.template.length > 0) {
            let prev = formValues.template[0]
            for (const value of formValues.template) {
                if (value !== prev) {
                    allDaysTheSame = false;
                }
                prev = value;
            }
    
        }

        return (
            <div className={style.container}  style={this.props.style}>
                <div className={clsx([style.topBar, style.topBarWithBackButton])}>
                    {this.props.progress && (<ProgressBar value={this.props.progress + step}/>)}
                    <Button variant="text" onClick={this.resetToDefault} className={style.backButton}>
                        <ArrowBackIcon fontSize="small"/>
                        <span style={{flex: 1}}>{tn.lang.back}</span>
                    </Button>
                </div>
                <div className={style.topContent}>
                    <Typography variant="h2">{tn.lang.newCalendarTemplateCustomTitle}</Typography>
                </div>
                <form className={style.formGuide} onSubmit={this.handleSubmit}>
                    <div className={style.formMiddleContent}>
                        <div className={style.formController}>
                            <Typography className={style.label}>{tn.lang.newCalendarRepeat}</Typography>
                            <Select
                                id="weeks"
                                name="weeks"
                                onChange={this.handleInputChange}
                                value={formValues.weeks}
                                native={isCordovaApp}
                                error={!!(formErrors.weeks)}
                            >
                                {weeksOptions.map((week) => {
                                    const Element = isCordovaApp ? 'option' : MenuItem;
                                    return (<Element
                                            key={week}
                                            value={week}
                                            selected={week === formValues.weeks}
                                        >
                                            {week}
                                        </Element>
                                    );
                                })}
                            </Select>
                            {formErrors.weeks &&
                                <Typography className={style.errorMessage}>{formErrors.weeks}</Typography>}
                        </div>
                        <div className={style.formController}>
                            <Typography>
                                {tn.lang.newCalendarTemplateCustomSubtitle}
                            </Typography>

                            <TemplateCalendar
                                    days={formValues.template}
                                    primaryColor={primaryColor}// {firstParent.color}
                                    secondaryColor={secondaryColor}// {secondParent.color}
                                    onDayClick={this.switchDay}
                                    showDayNames={true}
                                    size="large"
                                    localization={this.props.localization}
                                />
                            <div className={style.calendarTemplateLegend}>
                                <div className={style.calendarTemplateLegendItem}>
                                    <div className={style.calendarTemplateLegendItemCircle} style={{backgroundColor: primaryColor}} />
                                    <Typography variant="body2">{firstParent.firstName}</Typography>
                                </div>
                                <div className={style.calendarTemplateLegendItem}>
                                    <div className={style.calendarTemplateLegendItemCircle} style={{backgroundColor: secondaryColor}} />
                                    <Typography variant="body2">{secondParent.firstName}</Typography>
                                </div>
                            </div>
                            {formErrors.template &&
                                <Typography className={style.errorMessage}>{formErrors.template}</Typography>}
                        </div>
                    </div>
                    <div className={style.formBottomContent}>
                        {!this.state.oneChange && <Typography >{tn.lang.calWizardWhereSleeps}</Typography>}
                        <Button
                            disabled={allDaysTheSame && !this.state.oneChange}
                            type="submit"
                            variant='contained'
                            color='primary'
                            size="large"
                            fullWidth={true}
                        >
                            {tn.lang.continue}
                        </Button>
                    </div>
                </form>
            </div>
        );
    }

    private handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const {setTemplate, toggleSnackBar, setStepErrorType, setStep} = this.props;
        const {formValues} = this.state;
        const isTemplateValid = this.validateTemplate();

        if (isTemplateValid) {
            try {
                setTemplate(formValues.template);
                setStep(4);
            } catch (error) {
                toggleSnackBar(true);
                setStepErrorType(error);
            }
        }
    }

    private validateTemplate = (): boolean => {
        const {formValues} = this.state;

        if (!(formValues.template.length > 6)) {
            this.setState((prevState) => ({
                formErrors: {
                    ...prevState.formErrors,
                    template: tn.lang.CalendarTemplatesErrorTemplate,
                },
            }));
            return false;
        }

        this.setState((prevState) => ({
            formErrors: {
                ...prevState.formErrors,
                template: undefined,
            },
        }));
        return true;
    }

    private resetToDefault = () => {
        const {setStep} = this.props;
        this.setState(
            {
                formValues: {
                    template: new Array(14).fill(true),
                    weeks: 2
                },
                formErrors: {
                    template: undefined,
                    weeks: undefined,
                },
            }
        );

        if (this.props.goBack) {
            this.props.goBack();
        } else {
            setStep(2);
        }
    }

    private switchDay = (indexGiven: number) => {

        let index:number = indexGiven;

        if (this.props.localization === "en") {
            if (index === 0) {
                index = this.state.formValues.template.length -1;
            } else {
                index = index - 1;
            }
        }

        const {formValues} = this.state;
        const newTemplate = formValues.template.slice();
        newTemplate[index] = !newTemplate[index];

        this.setState((prevState) => ({
            oneChange:true,
            formValues: {
                ...prevState.formValues,
                template: newTemplate,
            },
        }));
    }

    private changeWeeks = (weeks: number) => {
        const {formValues} = this.state;
        const cleanWeeks = Math.min(14, Math.max(0, weeks));
        const newTemplate = formValues.template.slice();
        if (cleanWeeks * 7 > formValues.template.length) {
            newTemplate.length = cleanWeeks * 7;
            newTemplate.fill(true, formValues.template.length - 1, newTemplate.length);
        } else {
            newTemplate.length = cleanWeeks * 7;
        }
        this.setState({formValues: {weeks: cleanWeeks, template: newTemplate}});
    }

    private handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const {name, value, type, checked} = event.target;
        const inputValue = type === 'checkbox' ? checked : value;

        switch (name) {
            case 'weeks':
                this.changeWeeks(Number(inputValue));
                break;
            default:
                this.setState((prevState) => ({
                    formValues: {
                        ...prevState.formValues,
                        [name]: inputValue
                    }
                }));
                break;
        }

    }

}

StepTemplate.contextType = cordovaContext;

export default withStyles(GuideStyles)(StepTemplate);



// WEBPACK FOOTER //
// ./src/components/guide/steps/stepTemplateCustom.component.tsx